import React, { useState, useCallback } from 'react';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { BACKEND_URL } from '../utils/constants';
import { buildHeaders } from '../services/apiService';

const MapPinModal = ({ 
  isVisible, 
  userGPS, 
  onPinDropped, 
  onClose, 
  showToast 
}) => {
  const [selectedPin, setSelectedPin] = useState(null);
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  const [address, setAddress] = useState('');
  const [mapCenter, setMapCenter] = useState(null);
  const [nearbyBusiness, setNearbyBusiness] = useState(null);
  const [nearbyBusinesses, setNearbyBusinesses] = useState([]);  // ✅ Store multiple options
  const [showBusinessPicker, setShowBusinessPicker] = useState(false);  // ✅ Picker modal state
  const [isLookingUpBusiness, setIsLookingUpBusiness] = useState(false);

  // Google Maps JavaScript API key - MUST be set in environment variables
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  
  if (!GOOGLE_MAPS_API_KEY) {
    console.error('❌ REACT_APP_GOOGLE_MAPS_API_KEY not configured');
  }

  const mapContainerStyle = {
    width: '100%',
    height: '100%'
  };

  const defaultCenter = userGPS 
    ? { lat: userGPS.latitude, lng: userGPS.longitude }
    : { lat: 33.7490, lng: -84.3880 }; // Atlanta fallback

  const mapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
    styles: [
      // Dark theme for better visibility
      {
        featureType: 'all',
        elementType: 'geometry',
        stylers: [{ color: '#1e293b' }]  // Dark slate
      },
      {
        featureType: 'water',
        elementType: 'geometry',
        stylers: [{ color: '#00314F' }]  // PBFS Navy
      },
      {
        featureType: 'water',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#4b9fd5' }]
      },
      {
        featureType: 'road',
        elementType: 'geometry',
        stylers: [{ color: '#475569' }]  // Medium gray roads
      },
      {
        featureType: 'road',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#ffffff' }]
      },
      {
        featureType: 'landscape',
        elementType: 'geometry',
        stylers: [{ color: '#334155' }]  // Dark gray land
      },
      {
        featureType: 'poi',
        elementType: 'geometry',
        stylers: [{ color: '#3d5a3d' }]  // Dark green POIs
      },
      {
        featureType: 'poi.park',
        elementType: 'geometry',
        stylers: [{ color: '#1a4d2e' }]  // PBFS Green tint for parks
      },
      {
        featureType: 'all',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#e2e8f0' }]  // Light text
      },
      {
        featureType: 'all',
        elementType: 'labels.text.stroke',
        stylers: [{ color: '#000000' }, { weight: 2 }]  // Dark text outline
      }
    ]
  };

  const reverseGeocode = async (lat, lng) => {
    setIsReverseGeocoding(true);
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`
      );
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        const formattedAddress = data.results[0].formatted_address;
        setAddress(formattedAddress);
        setIsReverseGeocoding(false);
        return formattedAddress;
      } else {
        showToast('⚠️ Could not find address for this location', 'warning');
        setIsReverseGeocoding(false);
        return null;
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      showToast('❌ Error getting address', 'error');
      setIsReverseGeocoding(false);
      return null;
    }
  };

  const lookupNearbyBusiness = async (lat, lng) => {
    const safeLat = parseFloat(lat);
    const safeLng = parseFloat(lng);
    if (!Number.isFinite(safeLat) || safeLat < -90 || safeLat > 90 ||
        !Number.isFinite(safeLng) || safeLng < -180 || safeLng > 180) {
      setNearbyBusinesses([]);
      setNearbyBusiness(null);
      return;
    }
    setIsLookingUpBusiness(true);
    try {
      const params = new URLSearchParams({
        lat: String(safeLat),
        lng: String(safeLng),
        radius: '75',
      });
      const response = await fetch(
        `${BACKEND_URL}/api/places/nearby?${params.toString()}`,
        { method: 'GET', headers: buildHeaders() }
      );
      if (!response.ok) throw new Error(`Places lookup failed: ${response.status}`);
      const data = await response.json();
      if (data?.success && Array.isArray(data.places) && data.places.length > 0) {
        setNearbyBusinesses(data.places.slice(0, 5));
        setNearbyBusiness(data.places[0]);
      } else {
        setNearbyBusinesses([]);
        setNearbyBusiness(null);
      }
    } catch (e) {
      console.warn('Nearby business lookup failed:', e);
      setNearbyBusinesses([]);
      setNearbyBusiness(null);
    } finally {
      setIsLookingUpBusiness(false);
    }
  };

  const handleMapClick = useCallback(async (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    
    console.log('📍 Pin dropped at:', lat, lng);
    
    setSelectedPin({ lat, lng });
    setNearbyBusiness(null);
    setNearbyBusinesses([]);  // ✅ Reset picker list
    setShowBusinessPicker(false);  // ✅ Close picker if open
    
    // Get address from coordinates
    const addressResult = await reverseGeocode(lat, lng);
    await lookupNearbyBusiness(lat, lng);
    
    if (addressResult) {
      showToast('📍 Pin dropped! Drag to adjust or select a location option.', 'success');
    }
  }, []);

  const handlePinDrag = useCallback(async (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    
    console.log('📍 Pin dragged to:', lat, lng);
    
    setSelectedPin({ lat, lng });
    setNearbyBusiness(null);
    setNearbyBusinesses([]);  // ✅ Reset picker list on drag
    setShowBusinessPicker(false);  // ✅ Close picker on drag
    
    // Get new address while dragging
    const addressResult = await reverseGeocode(lat, lng);
    await lookupNearbyBusiness(lat, lng);
  }, []);

  const handleConfirmAddress = () => {
    if (!selectedPin || !address) {
      showToast('⚠️ Please drop a pin on the map first', 'warning');
      return;
    }

    // ✅ FIX: If we found a nearby business, use its name + the geocoded address
    // This captures BOTH business name and precise address from the pin
    const businessName = nearbyBusiness?.name || address;
    const businessPhone = nearbyBusiness?.phone || '';
    
    onPinDropped({
      name: businessName,  // ✅ Use business name if available
      address: address,    // Use geocoded address from pin
      phone: businessPhone,
      fullBusiness: { 
        name: businessName,
        address: address,
        phone: businessPhone,
        lat: selectedPin.lat,
        lng: selectedPin.lng,
        isMapPin: true,
        place_id: nearbyBusiness?.place_id
      },
      isManual: true,
      locationSource: 'pin_map'
    });

    // Reset state
    setSelectedPin(null);
    setAddress('');
    setNearbyBusiness(null);
  };

  const handleShowBusinessPicker = () => {
    if (!selectedPin || !address) {
      showToast('⚠️ Please drop a pin on the map first', 'warning');
      return;
    }
    
    if (nearbyBusinesses.length === 0) {
      showToast('⚠️ No nearby businesses found', 'warning');
      return;
    }
    
    setShowBusinessPicker(true);
  };

  const handleSelectBusiness = (business) => {
    if (!selectedPin) return;

    const name = business.name || address;
    const addr = business.address || address;
    const phone = business.phone || '';

    onPinDropped({
      name,
      address: addr,
      phone,
      fullBusiness: { 
        name,
        address: addr,
        phone,
        lat: selectedPin.lat,
        lng: selectedPin.lng,
        isMapPin: true,
        place_id: business.place_id,
        distance_m: business.distance_m
      },
      isManual: true,
      locationSource: 'pin_map'
    });

    // Reset state
    setSelectedPin(null);
    setAddress('');
    setNearbyBusiness(null);
    setNearbyBusinesses([]);
    setShowBusinessPicker(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/95 backdrop-blur-md z-[10000] flex flex-col">
      
      {/* Header */}
      <div className="bg-[var(--pbfs-navy)] border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[var(--pbfs-green)] tracking-wide">
            Drop Pin on Map
          </h2>
          <p className="text-white/60 text-sm mt-1">
            Tap anywhere to mark your location
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-white/60 hover:text-white text-2xl transition-all"
        >
          ✕
        </button>
      </div>

      {/* Map Container */}
      <div className="flex-1 relative">
        <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter || selectedPin || defaultCenter}
            zoom={18}
            onClick={handleMapClick}
            options={mapOptions}
            onLoad={(map) => {
              // Initialize map center to user's location
              if (userGPS) {
                setMapCenter({ lat: userGPS.latitude, lng: userGPS.longitude });
              }
            }}
          >
            {/* User's current location (blue dot) */}
            {userGPS && (
              <Marker
                position={{ lat: userGPS.latitude, lng: userGPS.longitude }}
                icon={{
                  path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                  fillColor: '#235EAC',
                  fillOpacity: 1,
                  strokeColor: '#ffffff',
                  strokeWeight: 3,
                  scale: 8
                }}
                title="Your location"
              />
            )}
            
            {/* Dropped pin (green) - DRAGGABLE with pulsing circle */}
            {selectedPin && (
              <>
                {/* Pulsing circle under pin for visibility */}
                <Marker
                  position={selectedPin}
                  icon={{
                    path: window.google?.maps?.SymbolPath?.CIRCLE,
                    fillColor: '#0DB14B',
                    fillOpacity: 0.3,
                    strokeColor: '#0DB14B',
                    strokeOpacity: 0.6,
                    strokeWeight: 2,
                    scale: 20
                  }}
                  zIndex={1}
                />
                
                {/* Main draggable pin - MUCH BIGGER & BRIGHTER */}
                <Marker
                  position={selectedPin}
                  draggable={true}
                  onDragEnd={handlePinDrag}
                  icon={{
                    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="64" viewBox="0 0 48 64">
                        <!-- Drop shadow -->
                        <ellipse cx="24" cy="60" rx="12" ry="4" fill="rgba(0,0,0,0.3)"/>
                        <!-- Pin body -->
                        <path d="M24 0 C14 0 6 8 6 18 C6 30 24 56 24 56 S42 30 42 18 C42 8 34 0 24 0 Z" 
                              fill="#0DB14B" stroke="#ffffff" stroke-width="3"/>
                        <!-- Inner circle -->
                        <circle cx="24" cy="18" r="8" fill="#ffffff"/>
                        <!-- Center dot -->
                        <circle cx="24" cy="18" r="4" fill="#00314F"/>
                      </svg>
                    `),
                    scaledSize: new window.google.maps.Size(48, 64),
                    anchor: new window.google.maps.Point(24, 64)
                  }}
                  animation={window.google?.maps?.Animation?.DROP}
                  title="Drag me to adjust location"
                  zIndex={10}
                />
              </>
            )}
          </GoogleMap>
        </LoadScript>

        {/* Recenter Button - Top Right */}
        {userGPS && (
          <button
            onClick={() => {
              setMapCenter({ lat: userGPS.latitude, lng: userGPS.longitude });
              showToast('📍 Centered on your location', 'success');
            }}
            className="absolute top-20 right-4 bg-[var(--pbfs-navy)]/95 backdrop-blur-xl hover:bg-[var(--pbfs-navy)] text-white p-3 rounded-full border border-[var(--pbfs-green)] shadow-xl transition-all duration-200 hover:scale-110 z-10"
            title="Center on my location"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
              <circle cx="12" cy="9" r="2.5" fill="currentColor" />
            </svg>
          </button>
        )}

        {/* Address Preview (if pin dropped) */}
        {selectedPin && (
          <div className="absolute bottom-6 left-6 right-6 bg-[var(--pbfs-navy)]/95 backdrop-blur-xl rounded-2xl p-4 border border-[var(--pbfs-green)] shadow-2xl">
            {isReverseGeocoding ? (
              <div className="text-white/70 text-sm text-center">
                🔍 Finding address...
              </div>
            ) : address ? (
              <>
                <div className="text-white text-sm leading-relaxed mb-2">
                  {address}
                </div>
                <div className="text-[var(--pbfs-green)] text-xs text-center">
                  💡 Drag the pin to adjust location
                </div>
              </>
            ) : (
              <div className="text-white/50 text-sm text-center">
                Tap the map to drop a pin, then drag to adjust
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Actions */}
      <div className="bg-[var(--pbfs-navy)] border-t border-white/10 px-6 py-4 flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 bg-white/10 hover:bg-white/15 text-white py-4 px-6 rounded-2xl transition-all duration-300"
        >
          Cancel
        </button>
        <button
          onClick={handleConfirmAddress}
          disabled={!selectedPin || !address || isReverseGeocoding || isLookingUpBusiness}
          className="flex-1 bg-white/10 hover:bg-white/15 disabled:bg-gray-600 disabled:opacity-50 text-white py-4 px-6 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-xl disabled:transform-none font-medium"
        >
          {(isReverseGeocoding || isLookingUpBusiness) ? '⏳ Loading...' : 'Use Address'}
        </button>
        <button
          onClick={handleShowBusinessPicker}
          disabled={!selectedPin || !address || isReverseGeocoding || isLookingUpBusiness || nearbyBusinesses.length === 0}
          className="flex-1 bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/90 disabled:bg-gray-600 disabled:opacity-50 text-white py-4 px-6 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-xl disabled:transform-none font-medium"
          title={nearbyBusinesses.length > 0 ? `${nearbyBusinesses.length} nearby businesses found` : 'No nearby businesses found'}
        >
          {(isReverseGeocoding || isLookingUpBusiness) ? '⏳ Loading...' : (nearbyBusinesses.length > 0 ? `Use Nearby Business (${nearbyBusinesses.length})` : 'No Business Found')}
        </button>
      </div>

      {/* ✅ Nearby Business Picker Modal */}
      {showBusinessPicker && (
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--pbfs-navy)] rounded-2xl max-w-md w-full max-h-[70vh] overflow-hidden border border-[var(--pbfs-green)] shadow-2xl">
            
            {/* Picker Header */}
            <div className="px-6 py-4 border-b border-white/10">
              <h3 className="text-lg font-bold text-[var(--pbfs-green)]">Select Nearby Business</h3>
              <p className="text-white/60 text-sm mt-1">
                {nearbyBusinesses.length} option{nearbyBusinesses.length !== 1 ? 's' : ''} found near your pin
              </p>
            </div>

            {/* Business List (scrollable) */}
            <div className="overflow-y-auto max-h-[50vh]">
              {nearbyBusinesses.map((business, idx) => {
                // Support both distance_meters (new) and distance_m (legacy)
                const distance = business.distance_meters || business.distance_m;
                const distanceText = distance 
                  ? (distance < 1000 
                      ? `${Math.round(distance)}m` 
                      : `${(distance / 1000).toFixed(1)}km`)
                  : '';
                
                // Visual indicators for ultra-close matches
                const isVeryClose = distance && distance < 50;
                const isClose = distance && distance >= 50 && distance < 150;
                
                return (
                  <button
                    key={business.place_id || idx}
                    onClick={() => handleSelectBusiness(business)}
                    className={`
                      w-full text-left px-6 py-4 hover:bg-white/10 border-b border-white/5 transition-all group
                      ${isVeryClose ? 'bg-[var(--pbfs-green)]/5' : ''}
                    `}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-semibold text-base group-hover:text-[var(--pbfs-green)] transition-colors truncate flex items-center gap-2">
                          {isVeryClose && <span className="text-lg">🎯</span>}
                          {business.name}
                        </div>
                        {business.address && (
                          <div className="text-white/60 text-sm mt-1 line-clamp-2">
                            {business.address}
                          </div>
                        )}
                        {business.phone && (
                          <div className="text-[var(--pbfs-blue)] text-sm mt-1">
                            📞 {business.phone}
                          </div>
                        )}
                      </div>
                      {distanceText && (
                        <div 
                          className={`
                            flex-shrink-0 text-xs font-semibold px-2 py-1 rounded-full
                            ${isVeryClose 
                              ? 'text-[var(--pbfs-green)] bg-[var(--pbfs-green)]/20 border border-[var(--pbfs-green)]/40' 
                              : isClose
                              ? 'text-blue-300 bg-blue-500/20 border border-blue-500/40'
                              : 'text-white/70 bg-white/10 border border-white/20'
                            }
                          `}
                        >
                          {distanceText}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Picker Footer */}
            <div className="px-6 py-4 border-t border-white/10">
              <button
                onClick={() => setShowBusinessPicker(false)}
                className="w-full bg-white/10 hover:bg-white/15 text-white py-3 px-4 rounded-lg transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapPinModal;

